'use client';
import React, { useEffect, useRef, useState } from 'react';

const credentials = [
  { label: 'Qualification', value: 'BDS, MDS (Prosthodontics)' },
  { label: 'Specialization', value: 'Prosthodontist & Implantologist' },
  { label: 'Experience', value: '5+ Years' },
  { label: 'Clinic', value: "DrPal\'s Dentistry" },
];

const expertise = [
  'Dental Veneers & Laminates',
  'Digital Smile Design',
  'Full Mouth Rehabilitation',
  'Dental Implants',
  'All-on-4 / All-on-6 Implants',
  'Crown & Bridge Work',
  'Occlusal Rehabilitation',
  'Cosmetic Dentistry',
];

export default function Doctor() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="doctor" ref={ref} className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section Label */}
        <div
          className={`inline-flex items-center gap-2 bg-secondary rounded-full px-4 py-1.5 mb-6 transition-all duration-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="font-inter text-xs font-semibold text-dark/60 uppercase tracking-widest">Our Doctor</span>
        </div>
        <h2
          className={`font-unbounded font-black text-dark text-3xl lg:text-4xl xl:text-5xl leading-[1.08] mb-14 transition-all duration-600 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          Dental Expert
          <br />
          You Can Trust
        </h2>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Doctor Image */}
          <div
            className={`relative transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="relative">
              {/* Background shape */}
              <div className="absolute inset-0 bg-primary/30 rounded-[40px] transform rotate-2" />
              <div className="relative rounded-[40px] overflow-hidden aspect-[3/4] max-w-sm mx-auto shadow-card-hover">
                <img
                  src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=600&q=85"
                  alt="Dr. Abhishek Pal - Prosthodontist and Implantologist at DrPal's Dentistry"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark/15 to-transparent" />
              </div>

              {/* Name card overlay */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-3rem)] bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-card-hover border border-secondary">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-unbounded font-bold text-dark text-sm">Dr. Abhishek Pal</p>
                    <p className="font-inter text-xs text-dark/50 mt-0.5">Prosthodontist & Implantologist</p>
                  </div>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5]?.map((s) => (
                      <svg key={s} width="12" height="12" viewBox="0 0 24 24" fill="#f59e0b">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Doctor Info */}
          <div
            className={`transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            {/* Credentials */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {credentials?.map((cred) => (
                <div key={cred?.label} className="bg-secondary rounded-2xl p-4 hover:bg-primary/30 transition-all duration-200">
                  <p className="font-inter text-xs text-dark/40 mb-1">{cred?.label}</p>
                  <p className="font-unbounded font-bold text-dark text-xs leading-snug">{cred?.value}</p>
                </div>
              ))}
            </div>

            {/* Bio */}
            <p className="font-inter text-dark/55 text-sm lg:text-base leading-relaxed mb-5">
              Dr. Abhishek Pal is a highly skilled Prosthodontist and Implantologist with over 5 years of
              dedicated clinical experience. Specializing in complex restorative and cosmetic dental procedures,
              he combines precision craftsmanship with an artistic eye to deliver smile transformations that
              look and feel completely natural.
            </p>
            <p className="font-inter text-dark/55 text-sm leading-relaxed mb-8">
              His patient-first philosophy ensures that every individual receives personalized attention,
              thorough explanations, and treatment plans designed around their unique needs and goals.
            </p>

            {/* Expertise Tags */}
            <div className="mb-8">
              <p className="font-unbounded font-bold text-dark text-xs mb-4 uppercase tracking-wide">Areas of Expertise</p>
              <div className="flex flex-wrap gap-2">
                {expertise?.map((skill) => (
                  <span
                    key={skill}
                    className="bg-secondary text-dark font-inter text-xs font-medium px-3 py-1.5 rounded-full hover:bg-primary hover:text-dark transition-all duration-200 cursor-default"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={() => {
                const el = document.querySelector('#contact');
                if (el) el?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-3 bg-dark text-white font-inter font-semibold text-sm px-8 py-4 rounded-full hover:bg-dark-light transition-all duration-200 hover:shadow-card-hover"
            >
              Book a Consultation
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#131c15" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
